import React, { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { withNamespaces } from 'react-i18next'
import moment from 'moment'
import logo from 'images/logos/logo.svg'

const BRAND = '#133A78'

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
    background: '#F4F6FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    overflowY: 'auto',
    fontFamily: 'Poppins, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 520,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 12px 40px rgba(19, 58, 120, 0.12)',
    padding: '40px 32px 28px',
    textAlign: 'center',
    color: '#1F2937',
  },
  logo: { height: 32, marginBottom: 28 },
  icon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'rgba(19, 58, 120, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: { fontSize: 22, fontWeight: 600, color: BRAND, margin: '0 0 10px' },
  text: { fontSize: 15, lineHeight: 1.6, color: '#4B5563', margin: '0 0 16px' },
  message: {
    background: '#F4F6FA',
    borderLeft: `4px solid ${BRAND}`,
    borderRadius: 8,
    padding: '12px 16px',
    textAlign: 'left',
    whiteSpace: 'pre-line',
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 16px',
  },
  until: { fontSize: 15, fontWeight: 600, color: '#1F2937', margin: '0 0 6px' },
  overdue: { fontSize: 13, color: '#92400E', margin: '0 0 6px' },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    marginTop: 24,
  },
  primary: {
    background: BRAND,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondary: {
    background: 'transparent',
    color: BRAND,
    border: `1px solid ${BRAND}`,
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tenants: { display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 12 },
  tenant: {
    background: '#fff',
    border: '1px solid #D1D5DB',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  footer: { fontSize: 12, color: '#9CA3AF', marginTop: 20 },
  banner: {
    position: 'fixed',
    left: '50%',
    bottom: 16,
    transform: 'translateX(-50%)',
    zIndex: 10000,
    maxWidth: 'calc(100% - 32px)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#FEF3C7',
    color: '#92400E',
    border: '1px solid #FCD34D',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
    fontFamily: 'Poppins, sans-serif',
  },
  bannerClose: {
    background: 'transparent',
    border: 'none',
    color: '#92400E',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
}

const WrenchIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14.7 6.3a4 4 0 0 0-5.4 5.1L3.6 17.1a1.5 1.5 0 0 0 0 2.1l1.2 1.2a1.5 1.5 0 0 0 2.1 0l5.7-5.7a4 4 0 0 0 5.1-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5z"
      stroke={BRAND}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
)

const MaintenanceScreen = ({ t, rootStore }) => {
  const { maintenanceStore, authStore } = rootStore
  const [showTenants, setShowTenants] = useState(false)
  const [bannerClosed, setBannerClosed] = useState(false)

  useEffect(() => {
    maintenanceStore.startBackgroundCheck()
  }, [maintenanceStore])

  if (!maintenanceStore.active) {
    if (!maintenanceStore.bypass || bannerClosed) return null
    return (
      <div style={styles.banner} role="status">
        <span>{t('maintenance.banner')}</span>
        <button
          type="button"
          style={styles.bannerClose}
          aria-label="Fechar"
          onClick={() => setBannerClosed(true)}
        >
          ×
        </button>
      </div>
    )
  }

  const until = maintenanceStore.until ? moment(maintenanceStore.until) : null
  const hasUntil = until && until.isValid()
  const overdue = hasUntil && until.isBefore(moment())
  const otherTenants = Array.isArray(authStore.availableTenants)
    ? authStore.availableTenants.filter((tenant) => tenant !== authStore.selectedTenant)
    : []

  const changeTenant = async (tenant) => {
    maintenanceStore.stopPolling()
    await authStore.login(tenant)
    setTimeout(async () => {
      await authStore.selectTenant(tenant)
    }, 1000)
  }

  return (
    <div style={styles.overlay} role="alertdialog" aria-live="polite">
      <div style={styles.card}>
        <img src={logo} alt="PlantScanner" style={styles.logo} />
        <div style={styles.icon}>
          <WrenchIcon />
        </div>
        <h1 style={styles.title}>{t('maintenance.title')}</h1>
        <p style={styles.text}>{t('maintenance.subtitle')}</p>

        {maintenanceStore.message && <div style={styles.message}>{maintenanceStore.message}</div>}

        {hasUntil && (
          <p style={styles.until}>{t('maintenance.until', { date: until.format('LLL') })}</p>
        )}
        {overdue && <p style={styles.overdue}>{t('maintenance.overdue')}</p>}

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.primary}
            disabled={maintenanceStore.checking}
            onClick={() => maintenanceStore.check()}
          >
            {maintenanceStore.checking ? t('maintenance.checking') : t('maintenance.checkNow')}
          </button>
          {otherTenants.length > 0 && (
            <button
              type="button"
              style={styles.secondary}
              onClick={() => setShowTenants(!showTenants)}
            >
              {t('maintenance.changeTenant')}
            </button>
          )}
          <button
            type="button"
            style={styles.secondary}
            onClick={() => {
              maintenanceStore.stopPolling()
              authStore.logout()
            }}
          >
            {t('maintenance.logout')}
          </button>
        </div>

        {showTenants && (
          <div style={styles.tenants}>
            {otherTenants
              .slice()
              .sort((a, b) => a.localeCompare(b))
              .map((tenant) => (
                <button
                  key={tenant}
                  type="button"
                  style={styles.tenant}
                  onClick={() => changeTenant(tenant)}
                >
                  {tenant}
                </button>
              ))}
          </div>
        )}

        {maintenanceStore.lastCheck && (
          <div style={styles.footer}>
            {t('maintenance.lastCheck', { time: moment(maintenanceStore.lastCheck).format('LT') })}
          </div>
        )}
      </div>
    </div>
  )
}

export default withNamespaces()(inject('rootStore')(observer(MaintenanceScreen)))
